import React from 'react';
import { ChecklistAnswerValue, ClientChecklistRun } from '../../types';
import { formatDublinTime } from '../../utils/date';
import { ANSWER_LABELS } from './ChecklistItemRow';

/**
 * Read-only body of one checklist run: every item with its answer (a legacy
 * per-item comment beneath, when a run carries one), the note for the
 * manager, and who started / signed it off. Shared by the admin sign-off
 * queue and the history view so both read the same record the same way.
 */
export const ChecklistRunDetails: React.FC<{ run: ClientChecklistRun }> = ({ run }) => {
  const answerByItem = new Map(run.answers.map((a) => [a.itemId, a]));
  return (
    <div
      data-testid={`checklist-run-details-${run.runId}`}
      className="space-y-2 border-t border-gray-100 px-4 pb-4 pt-3 text-sm"
    >
      {run.items.map((item) => {
        const answer = answerByItem.get(item.itemId);
        return (
          <div key={item.itemId} className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-700">{item.label}</span>
              <span
                data-testid={`checklist-detail-answer-${run.runId}-${item.itemId}`}
                className={`font-medium ${
                  !answer
                    ? 'text-gray-400'
                    : answer.value === ChecklistAnswerValue.NO
                    ? 'text-gray-900'
                    : 'text-gray-700'
                }`}
              >
                {answer ? ANSWER_LABELS[answer.value] : 'Not answered'}
              </span>
            </div>
            {answer?.comment && (
              <p className="border-l-2 border-gray-200 pl-3 text-xs text-gray-500">
                {answer.comment}
              </p>
            )}
          </div>
        );
      })}

      {run.note && (
        <div
          data-testid={`checklist-detail-note-${run.runId}`}
          className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
            Note for the manager
          </p>
          <p className="mt-0.5 whitespace-pre-line text-sm text-amber-900">{run.note}</p>
        </div>
      )}

      <p className="pt-1 text-xs text-gray-400">
        Started {formatDublinTime(run.startedAt)} by {run.startedByName}
        {run.signedOffAt && (
          <>
            {' · '}Signed off {formatDublinTime(run.signedOffAt)} by {run.signedOffByName}
          </>
        )}
      </p>
    </div>
  );
};
